import { ShiftAction } from 'components/RenderShiftActions/RenderShiftActions.types';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { useTranslation } from 'hooks/useTranslation';
import { MouseEvent, SetStateAction, useMemo } from 'react';
import { useSelector } from 'react-redux';
import schedulingReadAccessFacilityIdsSelector from 'store/selectors/scheduleSelectors/schedulingReadAccessFacilityIdsSelector';
import { Order, OrderStatusEnum, Review } from 'types/graphql';
import { getShiftBiddingStatus } from 'utils/shiftStatus';
import { useAutoExtendBid } from 'hooks/useAutoExtendBid';
import { isShiftBiddingFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { DateTime } from 'luxon';

export type CallOutShiftStatus = 'IN_PROGRESS' | 'PAUSED' | 'COMPLETE';

type BuildActionsForShiftCardParams = {
  order:
    | Order
    | (Order & {
        review?: Review | null;
      });
  setIsContinueCallOutConfirmationOpen: (value: SetStateAction<boolean>) => void;
  setIsAllowOvertimeConfirmationOpen: (value: SetStateAction<boolean>) => void;
  setIsAutoRebidConfirmationOpen: (value: SetStateAction<boolean>) => void;
  goToAwardTeamMemberFlow: (e: MouseEvent) => void;
  openSendToAgenciesFlow: (e: MouseEvent) => void;
  setIsBlockDeleteConfirmationOpen?: (value: SetStateAction<boolean>) => void;
};

type BuildActionsForShiftCardResult = {
  shiftActions: ShiftAction[];
  isShiftInfoWrapperVisible: boolean;
  callOutStatus: CallOutShiftStatus;
};

const noopSetState: (value: SetStateAction<boolean>) => void = () => {};

function createShiftAction(label: string, action: (e: unknown) => void): ShiftAction {
  return {
    label,
    action,
  };
}

type BuildActionsForShiftBlockCardParams = {
  status: OrderStatusEnum;
  biddingStatus: string | null;
  goToAwardTeamMemberFlow: (e: MouseEvent) => void;
  setIsBlockDeleteConfirmationOpen: (value: SetStateAction<boolean>) => void;
};

type BuildActionsForShiftBlockCardResult = {
  shiftActions: ShiftAction[];
};

function useBuildActionsForShiftBlockCard({
  status,
  biddingStatus,
  goToAwardTeamMemberFlow,
  setIsBlockDeleteConfirmationOpen,
}: BuildActionsForShiftBlockCardParams): BuildActionsForShiftBlockCardResult {
  const { t } = useTranslation();

  const shiftActions: ShiftAction[] = useMemo(() => {
    const actions: ShiftAction[] = [];
    if (biddingStatus === 'AWARDING') {
      actions.push(
        createShiftAction(
          t('dashboard:AWARD_BLOCK'),
          goToAwardTeamMemberFlow as (e: unknown) => void,
        ),
      );
    }
    if (status === OrderStatusEnum.pending) {
      actions.push(
        createShiftAction(t('dashboard:DELETE_BLOCK'), () =>
          setIsBlockDeleteConfirmationOpen(true),
        ),
      );
    }
    return actions;
  }, [t, status, biddingStatus, goToAwardTeamMemberFlow, setIsBlockDeleteConfirmationOpen]);

  return { shiftActions };
}

export function useBuildActionsForShiftCard({
  order,
  setIsContinueCallOutConfirmationOpen,
  setIsAllowOvertimeConfirmationOpen,
  setIsAutoRebidConfirmationOpen,
  goToAwardTeamMemberFlow,
  openSendToAgenciesFlow,
  setIsBlockDeleteConfirmationOpen,
}: BuildActionsForShiftCardParams): BuildActionsForShiftCardResult {
  const {
    allowOvertime,
    canSendToAgency,
    community,
    currentlyActiveCalloutList,
    isReviewAllowed,
    recurrence,
    review,
    sentToAgencies,
    status,
    biddable,
    bids,
    biddingTimeEndsAt,
    isExternal,
    awardingTimeStartsAt,
    autoRebid,
    timeZone,
  } = order;

  const orderTimeZone = `${timeZone}`;

  const { t } = useTranslation();

  const isReadOnly = useSelector(
    FEATURE_SELECTORS_MAP['FEATURE_TYPE_YMCA_READ_ONLY_SECONDARY_USER'],
  );
  const isShiftBiddingFeatureEnabled = useSelector(isShiftBiddingFeatureEnabledSelector);
  const locationsWithScheduling = useSelector(schedulingReadAccessFacilityIdsSelector);
  const isSchedulingEnabled = useMemo(
    () => locationsWithScheduling.includes(Number(community.id)),
    [community.id, locationsWithScheduling],
  );

  const isCalloutPaused = useMemo(
    () =>
      currentlyActiveCalloutList?.waitingForManualApproval &&
      !currentlyActiveCalloutList?.isCompleted &&
      status.value === OrderStatusEnum.pending &&
      !(isShiftBiddingFeatureEnabled && order.biddable),
    [
      currentlyActiveCalloutList?.isCompleted,
      currentlyActiveCalloutList?.waitingForManualApproval,
      isShiftBiddingFeatureEnabled,
      order.biddable,
      status.value,
    ],
  );

  const isCalloutComplete = useMemo(
    () => currentlyActiveCalloutList?.isCompleted && status.value === OrderStatusEnum.pending,
    [currentlyActiveCalloutList?.isCompleted, status.value],
  );

  const isAllowOvertimeMenuItem =
    !recurrence &&
    !allowOvertime &&
    !biddable &&
    status.value === OrderStatusEnum.pending &&
    !isExternal &&
    isSchedulingEnabled;

  const biddingStatus = getShiftBiddingStatus({
    status: status.value,
    biddable,
    biddingTimeEndsAt,
    awardingTimeStartsAt,
    bids,
    timeZone: orderTimeZone,
  });

  const { isAutoBidAllowed } = useAutoExtendBid({
    biddable,
    autoRebid,
    sentToAgencies,
    bids,
    status: status.value,
    biddingTimeEndsAt,
    timeZone: orderTimeZone,
  });

  const { shiftActions: blockShiftActions } = useBuildActionsForShiftBlockCard({
    status: status.value,
    biddingStatus,
    goToAwardTeamMemberFlow,
    setIsBlockDeleteConfirmationOpen: setIsBlockDeleteConfirmationOpen || noopSetState,
  });

  if (order.isBlockShift) {
    return {
      shiftActions: blockShiftActions,
      isShiftInfoWrapperVisible: true,
      callOutStatus: 'IN_PROGRESS',
    };
  }

  const canSendShiftToAgency = !!canSendToAgency && !sentToAgencies;
  const bidsLength = bids?.length;
  const isAwardingTimeStarted =
    DateTime.fromISO(awardingTimeStartsAt, { zone: orderTimeZone }) < DateTime.now();
  const isShiftAllowedToSendToAgency = biddable
    ? canSendShiftToAgency && bidsLength === 0 && isAwardingTimeStarted
    : canSendShiftToAgency;

  const isShiftInfoWrapperVisible =
    !isReadOnly &&
    (!!isReviewAllowed ||
      !!canSendToAgency ||
      !!isCalloutPaused ||
      !!isAllowOvertimeMenuItem ||
      !!review?.rating ||
      biddingStatus === 'AWARDING' ||
      isAutoBidAllowed);

  const actions: ShiftAction[] = [];
  if (!!isCalloutPaused)
    actions.push(
      createShiftAction(t('dashboard:CONTINUE_CALL_OUT'), () =>
        setIsContinueCallOutConfirmationOpen(true),
      ),
    );
  if (!!isAutoBidAllowed)
    actions.push(
      createShiftAction(t('create_shifts:AUTO_EXTEND_BID'), () =>
        setIsAutoRebidConfirmationOpen(true),
      ),
    );
  if (isShiftAllowedToSendToAgency)
    actions.push(
      createShiftAction(
        t('schedule:SEND_TO_AGENCY'),
        openSendToAgenciesFlow as (e: unknown) => void,
      ),
    );
  if (biddingStatus === 'AWARDING')
    actions.push(
      createShiftAction(
        t('dashboard:AWARD_SHIFT'),
        goToAwardTeamMemberFlow as (e: unknown) => void,
      ),
    );
  if (!!isAllowOvertimeMenuItem)
    actions.push(
      createShiftAction(t('dashboard:ALLOW_OVERTIME'), () =>
        setIsAllowOvertimeConfirmationOpen(true),
      ),
    );
  return {
    shiftActions: actions,
    isShiftInfoWrapperVisible,
    callOutStatus: isCalloutPaused ? 'PAUSED' : isCalloutComplete ? 'COMPLETE' : 'IN_PROGRESS',
  };
}
